// @ts-check
/**
 * @file NewPasswordScreen.jsx
 * @location src/components/auth/NewPasswordScreen.jsx
 * @role 패스워드 리셋 확정 화면 — 사용자가 메일 링크 클릭 후 새 패스워드 설정.
 *   SupabaseGate 가 auth.passwordRecovery=true 일 때 본 화면을 렌더.
 */
import { useState } from 'react';
import { PasswordField } from './PasswordField.jsx';
import { localizeAuthError } from '../../integrations/supabase/errorMessage.js';
import { validatePassword, PASSWORD_HINT } from '../../utils/passwordPolicy.js';

/**
 * @param {{
 *   onUpdate: (newPassword: string) => Promise<void>,
 *   onExit?: () => void,
 * }} props
 */
export function NewPasswordScreen({ onUpdate, onExit }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (busy) return;
    const v = validatePassword(password);
    if (!v.ok) { setErr(v.message); return; }
    setBusy(true); setErr('');
    try {
      await onUpdate(password);
      setDone(true);
      // 「완료」 메시지가 보일 시간 확보 후 recovery 종료(이 화면을 unmount).
      setTimeout(() => { onExit?.(); }, 1500);
    } catch (e) {
      setErr(localizeAuthError(e, '갱신에 실패했습니다. 잠시 후 다시 시도해 주세요.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="modal auth-modal">
        <div className="mhd"><span className="mtit">온점</span></div>
        <div className="mbody">
          {done ? (
            <p className="auth-lead auth-sent">
              비밀번호를 갱신했습니다.<br />일반 로그인이 가능합니다.
            </p>
          ) : (
            <>
              <p className="auth-lead">새 비밀번호를 설정해 주세요 ({PASSWORD_HINT}).</p>
              <PasswordField
                value={password}
                onChange={setPassword}
                autoComplete="new-password"
              />
              {err && <div className="auth-err">{err}</div>}
              <button
                type="button"
                className="auth-primary"
                onClick={submit}
                disabled={busy}
              >갱신</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
